import { useState, useEffect, useCallback, useRef } from 'react';
import { readContract } from '@wagmi/core';
import MultiCallAbi from '../config/MultiCallAbi.json';
import LaunchpadCard from '../components/LaunchpadCard';
import ClipLoader from 'react-spinners/ClipLoader';
import TopBar from '../components/TopBar';
import { Link, useLocation } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { imageUrl, ethPriceApiUrl, supportedChainIds, chainLogos, projectName } from '../utils/constants.ts';
import { getMulticallAddress } from '../utils/addressHelpers.ts';
import Slider from '../components/Slider.jsx';
import Web3 from 'web3';
import rot13 from '../utils/encode.ts';
import { useQueryParam, StringParam } from 'use-query-params';
import { config } from '../config.jsx'
import Footer from '../components/Footer';

// Helper component for token logo with fallback support
const TokenLogo = ({ logoUrl, tokenName, className = "w-8 h-8 rounded-full", alt = "" }) => {
  const [currentUrl, setCurrentUrl] = useState(logoUrl);
  const [hasTriedFallback, setHasTriedFallback] = useState(false);

  // Reset when logoUrl changes
  useEffect(() => {
    setCurrentUrl(logoUrl);
    setHasTriedFallback(false);
  }, [logoUrl]);

  // Helper function to get fallback URL
  const getFallbackUrl = (url, tokenName) => {
    if (!url || !tokenName) return url;
    try {
      const parts = url.split('/');
      const filename = parts[parts.length - 1];
      const escapedTokenName = tokenName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const fallbackFilename = filename.replace(new RegExp(`^${escapedTokenName}-`), `${projectName}-`);
      if (fallbackFilename !== filename) {
        parts[parts.length - 1] = fallbackFilename;
        return parts.join('/');
      }
    } catch (error) {
      console.error('Error constructing fallback URL:', error);
    }
    return url;
  };

  const handleError = () => {
    if (!hasTriedFallback && logoUrl) {
      setHasTriedFallback(true);
      const fallback = getFallbackUrl(logoUrl, tokenName);
      if (fallback !== logoUrl) {
        setCurrentUrl(fallback);
      }
    }
  };

  if (!currentUrl) return null;

  return (
    <img
      src={currentUrl}
      className={className}
      alt={alt || tokenName}
      onError={handleError}
    />
  );
};

const App = () => {
  let [loading, setLoading] = useState(false);
  const [funLists, setFunListData] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalLength, setTotalLength] = useState(0);
  const [allLists, setAllLists] = useState([]);
  const cookies = new Cookies();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  let [ref] = useQueryParam('ref', StringParam);
  const location = useLocation();
  const fetchDataRef = useRef(null);

  // Format price with 6 decimal places
  const formatPrice = (num) => {
    if (!num || isNaN(num)) return '0.000000';
    return num.toFixed(6);
  };

  if (ref) {
    if (Web3.utils.isAddress(rot13(ref))) {
      cookies.set('ref', ref);
    }
  }

  const FetchData = useCallback(async () => {
      try {
        setLoading(true);
      setFunListData([]); // Reset state before fetching new data
      const allFunData = []; // Collect all data first
      const allTrendingData = []; // Collect trending data
      const allPromotedAddresses = []; // Collect promoted addresses

        for (const chainId of supportedChainIds) {
        try {
          const ethPriceResponse = await fetch(ethPriceApiUrl[chainId], { method: 'GET' });
          const ethPriceData = await ethPriceResponse.json();
          const ethPrice = Object.values(ethPriceData)[0]?.usd;

          const mainInfo = await readContract(config, {
            address: getMulticallAddress(chainId),
            abi: MultiCallAbi,
            functionName: 'getMainInfo',
            chainId: chainId,
          });

          const otherInfo = await readContract(config, {
            address: getMulticallAddress(chainId),
            abi: MultiCallAbi,
            functionName: 'getOtherInfo',
            chainId: chainId,
          });

          const restInfo = await readContract(config, {
            address: getMulticallAddress(chainId),
            abi: MultiCallAbi,
            functionName: 'getRestInfo',
            chainId: chainId,
          });

          // Fetch trending data
          try {
            const trendingInfo = await readContract(config, {
              address: getMulticallAddress(chainId),
              abi: MultiCallAbi,
              functionName: 'funTrending',
              chainId: chainId,
            });

            if (trendingInfo && trendingInfo[2] && trendingInfo[2].length > 0) {
              // trendingInfo[0] = uint256[] (indices or market caps)
              // trendingInfo[1] = string[] (token names)
              // trendingInfo[2] = address[] (token addresses)
              const trendingAddresses = trendingInfo[2];
              const trendingNames = trendingInfo[1] || [];
              const trendingValues = trendingInfo[0] || [];

              for (let i = 0; i < trendingAddresses.length; i++) {
                allTrendingData.push({
                  chainId,
                  address: trendingAddresses[i],
                  name: trendingNames[i] || '',
                  value: trendingValues[i] ? Number(trendingValues[i]) : 0,
                });
              }
            }
          } catch (trendingError) {
            console.error(`Error fetching trending data for chain ${chainId}:`, trendingError);
          }

          // Fetch promoted addresses (getUseSocialAddresses)
          try {
            const promotedInfo = await readContract(config, {
              address: getMulticallAddress(chainId),
              abi: MultiCallAbi,
              functionName: 'getUseSocialAddresses',
              chainId: chainId,
            });

            if (promotedInfo && promotedInfo.length > 0) {
              for (let i = 0; i < promotedInfo.length; i++) {
                allPromotedAddresses.push({
                  chainId,
                  address: promotedInfo[i].toLowerCase(),
                });
              }
            }
          } catch (promotedError) {
            console.error(`Error fetching promoted addresses for chain ${chainId}:`, promotedError);
          }

          if (mainInfo && mainInfo[0] && mainInfo[0].length > 0) {
            for (let i = mainInfo[0].length - 1; i >= 0; i--) {
              let startTime = Number(mainInfo[0][i]);
              const lpCreated = restInfo[1][i];
              let progress;
              const contractAddress = mainInfo[5][i];
              const virtualLpAmounts = Number(mainInfo[2][i]) * ethPrice;
              const virtualLpTokenAmounts = Number(mainInfo[1][i]) / 10 ** 18;
              const tokenPrice = Number(mainInfo[3][i]);
              const marketCap = (tokenPrice * 1000000000 * Number(ethPrice)) / 10 ** 12;
              // console.log("marketCap", tokenPrice, ethPrice, marketCap);
              const price = (tokenPrice * Number(ethPrice)) / 10 ** 12;
              const website = otherInfo[2][i];
              const twitter = otherInfo[3][i];
              const telegram = otherInfo[4][i];
              const liquidity = virtualLpAmounts * 2;
              const name = otherInfo[0][i];
              const symbol = otherInfo[1][i];
              const description = restInfo[2][i];
              // Support both formats:
              // 1. Privacy wallet tokens: tokenName-chainId-presaleAddress.png (backend uses token name)
              // 2. Normal tokens: FlazePad-chainId-presaleAddress.png (frontend uses constant)
              // Try token name format first (for privacy wallet), LaunchpadCard will fallback to projectName format
              const logoUrl = imageUrl + 'logo/' + name + "-" + chainId + "-" + contractAddress + '.png';
              const bannerUrl = imageUrl + 'banner/' + name + "-" + chainId + "-" + contractAddress + '.png';
              // Also create fallback URLs with projectName format for normal tokens
              const logoUrlFallback = imageUrl + 'logo/' + projectName + "-" + chainId + "-" + contractAddress + '.png';
              const bannerUrlFallback = imageUrl + 'banner/' + projectName + "-" + chainId + "-" + contractAddress + '.png';
              let blockchainLogoUrl = chainLogos[chainId];
              let devAddress = restInfo[4][i];
              let raisingPercent = Number(otherInfo[5][i]) / 100;
              // progress = lpCreated ? 100 : (raisingPercent > 0 ? (marketCap * 100) / 116000 : 0);
              progress = lpCreated ? 100 : (virtualLpTokenAmounts < 1072999995 ? (marketCap * 100) / 116000 : 0);
              let dexAddress = restInfo[3][i];
              let dexName = 'PancakeSwap';
              const funData = {
                chainId,
                startTime: startTime,
                progress: progress,
                Liquidity: liquidity,
                tokenName: name,
                tokenSymbol: symbol,
                tokenDescription: description,
                logoUrl: logoUrl,
                bannerUrl: bannerUrl,
                address: mainInfo[5][i],
                depositedAmount: (Number(mainInfo[4][i]) * ethPrice) / 10 ** 18,
                contractAddress: contractAddress,
                dexAddress: dexAddress,
                devAddress: devAddress,
                dexName: dexName,
                marketCap: marketCap,
                price: price,
                volume: (Number(mainInfo[4][i]) * ethPrice) / 10 ** 18,
                website: website,
                twitter: twitter,
                telegram: telegram,
                blockchainLogoUrl: blockchainLogoUrl,
                raisingPercent: raisingPercent,
                lpCreated,
              };
              allFunData.push(funData);
            }
          }
        } catch (chainError) {
          console.error(`Error fetching data for chain ${chainId}:`, chainError);
          // Continue with other chains even if one fails
          }
        }

      // Set all data at once
      setFunListData(allFunData);
      setTrendingData(allTrendingData);
      setPromotedAddresses(allPromotedAddresses);
        setLoading(false);
      } catch (e) {
        setLoading(false);
      console.error('Error in FetchData:', e);
      }
  }, []);

  // Fetch data on mount and when location changes (user navigates to board page)
  useEffect(() => {
    FetchData();
  }, [FetchData, location.pathname]);

  const sortOptions = [
    { value: 'Creation Time', label: 'Creation Time' },
    { value: 'Trending', label: 'Trending' },
    { value: 'Promoted', label: 'Promoted' },
    { value: 'Market Cap', label: 'Market Cap' },
    { value: 'Migrated', label: 'Migrated' },
  ];

  const orderOptions = [
    { value: 'Descending', label: 'Descending' },
    { value: 'Ascending', label: 'Ascending' },
  ];

  const [sortedFunLists, setSortedFunLists] = useState([]);
  const [filteredFunLists, setFilteredFunLists] = useState([]);
  const [sortValue, setSortValue] = useState(sortOptions[0]);
  const [orderValue, setOrderValue] = useState(orderOptions[0]);
  const [trendingData, setTrendingData] = useState([]);
  const [promotedAddresses, setPromotedAddresses] = useState([]);

  const filterFunLists = useCallback(() => {
    const searchFiltered = funLists.filter((list) => {
      if (!search || search === '') {
        return true;
      }

      const searchLower = search.toLowerCase().trim();
      const searchTrimmed = search.trim();

      // Search by token name (case-insensitive partial match)
      if (list.tokenName && list.tokenName.toLowerCase().includes(searchLower)) {
        return true;
      }

      // Search by token symbol (case-insensitive partial match)
      if (list.tokenSymbol && list.tokenSymbol.toLowerCase().includes(searchLower)) {
        return true;
      }

      // Search by presale address (address field) - supports both partial and exact match
      if (list.address) {
        const addressLower = list.address.toLowerCase();
        // Partial match
        if (addressLower.includes(searchLower)) {
          return true;
        }
        // Exact match (case-insensitive)
        if (addressLower === searchLower) {
          return true;
        }
      }

      // Search by contract address - supports both partial and exact match
      if (list.contractAddress) {
        const contractAddressLower = list.contractAddress.toLowerCase();
        // Partial match
        if (contractAddressLower.includes(searchLower)) {
          return true;
        }
        // Exact match (case-insensitive)
        if (contractAddressLower === searchLower) {
          return true;
        }
      }

      // Search by dev address (creator address) - supports both partial and exact match
      if (list.devAddress) {
        const devAddressLower = list.devAddress.toLowerCase();
        if (devAddressLower.includes(searchLower) || devAddressLower === searchLower) {
          return true;
        }
      }

      // If search looks like an Ethereum address (starts with 0x and has valid length)
      if (searchTrimmed.startsWith('0x') && searchTrimmed.length >= 10) {
        // Try to match any address field with the search term
        const addressFields = [
          list.address,
          list.contractAddress,
          list.devAddress,
          list.tokenAddress
        ].filter(Boolean); // Remove undefined/null values

        for (const addr of addressFields) {
          if (addr && addr.toLowerCase().includes(searchLower)) {
        return true;
      }
        }
      }

      return false;
    });
    setFilteredFunLists(searchFiltered);
  }, [funLists, search]);

  const onSearchChanged = (event) => {
    setSearch(event.target.value);
  };

  const sortFunLists = useCallback(async () => {
    let sortedList = [];
    switch (sortValue.value) {
      case 'Trending':
        // For Trending, filter funLists to only include trending addresses
        const trendingAddresses = trendingData.map(t => t.address.toLowerCase());
        const trendingList = filteredFunLists.filter(item => 
          trendingAddresses.includes(item.address.toLowerCase())
        );
        
        // Sort by rise status (raisingPercent) instead of trending order
        sortedList = trendingList.sort((a, b) => {
          const aRise = a.raisingPercent || 0;
          const bRise = b.raisingPercent || 0;
          if (orderValue.value === 'Ascending') {
            return aRise - bRise;
          } else {
            return bRise - aRise;
          }
        });
        break;
      case 'Creation Time':
        sortedList = [...filteredFunLists].sort((a, b) => {
          if (orderValue.value === 'Ascending') {
            return a.startTime - b.startTime;
          } else {
            return b.startTime - a.startTime;
          }
        });
        break;
      case 'Promoted':
        // For Promoted, filter funLists to only include promoted addresses
        const promotedAddressList = promotedAddresses.map(p => p.address.toLowerCase());
        const promotedList = filteredFunLists.filter(item => 
          promotedAddressList.includes(item.address.toLowerCase())
        );
        
        // Sort by creation time (newest first by default)
        sortedList = promotedList.sort((a, b) => {
          if (orderValue.value === 'Ascending') {
            return a.startTime - b.startTime;
          } else {
            return b.startTime - a.startTime;
          }
        });
        break;
      case 'Market Cap':
        // Sort by market cap
        sortedList = [...filteredFunLists].sort((a, b) => {
          const aMarketCap = a.marketCap || 0;
          const bMarketCap = b.marketCap || 0;
          if (orderValue.value === 'Ascending') {
            return aMarketCap - bMarketCap;
          } else {
            return bMarketCap - aMarketCap;
          }
        });
        break;
      case 'Migrated':
        // For Migrated, show only listed projects (progress >= 100)
        sortedList = [...filteredFunLists].filter((item) => item.progress >= 100).sort((a, b) => {
          if (orderValue.value === 'Ascending') {
            return a.startTime - b.startTime;
          } else {
            return b.startTime - a.startTime;
          }
        });
        break;
      default:
        break;
    }
    setAllLists(sortedList);
    let last;
    let length = sortedList.length;
    setTotalLength(length);
    let datas = [];
    if (length > 24) {
      last = 24;
    } else {
      last = length;
    }
    for (let i = 0; i < last; i++) {
      datas.push(sortedList[i]);
    }
    setCurrentIndex(last);
    setSortedFunLists(datas);
  }, [orderValue, sortValue, filteredFunLists, trendingData, promotedAddresses]);

  useEffect(() => {
    setFilteredFunLists([...funLists]);
  }, [funLists]);

  useEffect(() => {
    filterFunLists();
  }, [filterFunLists]);

  useEffect(() => {
    sortFunLists();
  }, [orderValue, sortValue, filteredFunLists, sortFunLists]);

  const onSortChange = (newValue) => {
    setSortValue(newValue);
    sortFunLists();
  };

  const onOrderChange = () => {
    if (orderValue.value === 'Descending') {
      setOrderValue(orderOptions[1]);
    } else {
      setOrderValue(orderOptions[0]);
    }
    sortFunLists();
  };


  const loadMoreLists = () => async () => {
    try {
      setLoading(true);
      let newLists = [];
      let last;
      const length = totalLength - currentIndex;
      if (length >= 0) {
        if (length > 24) {
          last = currentIndex + 24;
        } else {
          last = currentIndex + length;
        }
        for (let i = currentIndex; i < last; i++) {
          newLists.push(allLists[i]);
        }
        let newDatas = sortedFunLists.concat(newLists);
        setSortedFunLists(newDatas);
        setCurrentIndex(last);
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.error(e);
    }
  };

  const LaunchpadCardGrid = ({ items }) => {
    return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.map(
          (
            {
              chainId,
              progress,
              Liquidity,
              tokenName,
              tokenSymbol,
              tokenDescription,
              logoUrl,
              bannerUrl,
              address,
              depositedAmount,
              contractAddress,
              dexAddress,
              devAddress,
              dexName,
              marketCap,
              price,
              volume,
              startTime,
              website,
              twitter,
              telegram,
              blockchainLogoUrl,
              raisingPercent,
            },
            i
          ) => (
            <LaunchpadCard
              key={`${chainId}-${contractAddress}-${i}`}
              chainId={chainId}
              progress={progress}
              Liquidity={Liquidity}
              tokenName={tokenName}
              tokenSymbol={tokenSymbol}
              tokenDescription={tokenDescription}
              logoUrl={logoUrl}
              bannerUrl={bannerUrl}
              funAddress={address}
              depositedAmount={depositedAmount}
              contractAddress={contractAddress}
              dexAddress={dexAddress}
              devAddress={devAddress}
              dexName={dexName}
              marketCap={marketCap}
              price={price}
              volume={volume}
              startTime={startTime}
              website={website}
              twitter={twitter}
              telegram={telegram}
              blockchainLogoUrl={blockchainLogoUrl}
              raisingPercent={raisingPercent}
            />
          )
        )}
      </div>
    );
  };

  const toggleModal = () => {
    setModalIsOpen(!modalIsOpen);
    cookies.set('show-how-it-works', 'true');
  };

  const closeModal = (e) => {
    if (e.target.id === 'modal') {
      setModalIsOpen(false);
      cookies.set('show-how-it-works', 'true');
    }
  };

  const modalContent = (
    <div id="modal" onClick={closeModal} className={`modal ${modalIsOpen ? 'show-modal' : ''}`}>
      <div className="rounded-xl p-6 max-w-md" style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--border-color)',
        boxShadow: 'var(--shadow)'
      }}>
        <h1 className="text-center text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          All created tokens in MemeLaunch are safe!
        </h1>
        <ul className="space-y-2 mb-4" style={{ color: 'var(--text-secondary)' }}>
          <li>✅ Each coin is a fair-launch with no presale and no team allocation.</li>
          <li>✅ Everyone has equal chance!</li>
        </ul>
        <h1 className="text-center text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>How it works</h1>
        <ol className="space-y-2 mb-4" style={{ color: 'var(--text-secondary)' }}>
          <li>1️⃣ Pick a coin that you like</li>
          <li>2️⃣ Buy the coin on the bonding curve</li>
          <li>3️⃣ Sell at any time to lock in your profits or losses</li>
          <li>4️⃣ When enough people buy, it reaches $69k market cap and gets listed on DEX</li>
          <li>5️⃣ LP Tokens will be burned🔥</li>
        </ol>
        <p className="italic text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
          Disclaimer: Trading tokens is highly risky / speculative. Do not invest more than you can afford to lose.
        </p>
        <div className="text-center">
          <button
            onClick={toggleModal}
            className="px-6 py-3 rounded-lg font-medium transition-all"
            style={{
              background: 'var(--primary-gradient)',
              color: 'var(--button-text)',
            }}
          >
            I'm ready to pump
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen" style={{ background: 'var(--page-bg)' }}>
      <div className="flex-grow">
      <style jsx>{`
        .modal {
          opacity: 0;
          visibility: hidden;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          transition: all 0.3s ease;
          z-index: 100;
        }

        .show-modal {
          opacity: 1;
          visibility: visible;
        }
      `}</style>
      {modalContent}
      <TopBar />
      <div className="max-w-7xl mx-auto pt-28 pb-16 px-4 sm:px-6">
        {/* Top 3 Trending Tokens - Create Token button + cards */}
        <div className="flex flex-col md:flex-row justify-center md:items-center gap-6 mb-12">
          <div className="flex justify-center md:justify-center shrink-0">
            <Link
              to="/create"
              className="p-3 rounded-xl flex items-center justify-center font-bold transition-all shadow-lg hover:shadow-xl hover:scale-105 w-[150px] h-[80px] min-w-[150px]"
              style={{
                background: '#ffffff',
                border: '1px solid #e5e5e5',
                color: '#000000'
              }}
            >
              <span style={{ fontSize: '16px', color: '#000000', fontWeight: 'bold' }}>
                Create Token
              </span>
            </Link>
          </div>
          <div className="inline-grid grid-cols-1 sm:grid-cols-3 gap-6 w-full max-w-6xl md:flex-1 min-w-0">
          {(() => {
            // Get top 3 trending tokens
            const trendingAddresses = trendingData.map(t => t.address.toLowerCase());
            const trendingTokens = funLists
              .filter(item => trendingAddresses.includes(item.address.toLowerCase()))
              .map(item => {
                const trendingInfo = trendingData.find(t => t.address.toLowerCase() === item.address.toLowerCase());
                return {
                  ...item,
                  trendingValue: trendingInfo?.value || 0
                };
              })
              .sort((a, b) => b.trendingValue - a.trendingValue)
              .slice(0, 3);

            if (loading) {
              return [1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="px-8 py-0 rounded-2xl flex items-center justify-center bg-black border border-gray-800"
                >
                  <ClipLoader color="var(--text-accent)" size={32} />
                </div>
              ));
            }

            if (trendingTokens.length === 0) {
              return (
                <div
                  className="col-span-3 p-8 rounded-2xl text-center bg-black border border-gray-800 text-lg"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  No Trending Tokens Available
                </div>
              );
            }

            return trendingTokens.map((token, index) => (
              <Link
                key={`${token.chainId}-${token.address}-${index}`}
                to={`/buy/?chain=${token.chainId}&address=${token.address}`}
                className="group flex items-center gap-6 pl-0 pr-8 py-0 rounded-2xl bg-black border border-gray-800 transition-all duration-200 hover:bg-gray-900 hover:border-gray-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-gray-600 focus:ring-offset-2 focus:ring-offset-transparent"
                style={{ cursor: 'pointer' }}
              >
                {/* Logo */}
                <div className="flex-shrink-0 w-44 h-44 rounded-xl overflow-hidden bg-gray-800">
                  <TokenLogo
                    logoUrl={token.logoUrl}
                    tokenName={token.tokenName}
                    className="w-full h-full rounded-xl object-cover"
                    alt={token.tokenName}
                  />
                </div>
                {/* Name, ticker, price - full text with wrap */}
                <div className="flex-1 min-w-0 text-left">
                  <h3 className="font-semibold text-xl mb-1 break-words" style={{ color: 'var(--text-primary)' }}>
                    {token.tokenName}
                  </h3>
                  <p className="text-base opacity-80 mb-1 break-words" style={{ color: 'var(--text-secondary)' }}>
                    {token.tokenSymbol || 'N/A'}
                  </p>
                  <p className="text-lg font-medium break-all" style={{ color: 'var(--text-accent)' }}>
                    ${token.price ? formatPrice(token.price) : '0.000000'}
                  </p>
                </div>
              </Link>
            ));
          })()}
          </div>
        </div>

        {/* Listed Projects */}
        {/* {funLists.filter((item) => item.progress >= 100).length > 0 && (
          <div className="mb-12">
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Listed Projects</h2>
            <Slider items={funLists.filter((item) => item.progress >= 100)} isListed={true} />
          </div>
        )} */}

        {/* Filters */}
        <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center mb-8">
          <div className="flex flex-wrap gap-2 items-center">
            {sortOptions.map((option) => (
              <button
                key={option.value}
                className={`px-4 py-2 rounded-full font-medium transition-all ${sortValue.value === option.value
                    ? 'text-white'
                    : 'text-gray-500'
                  }`}
                onClick={() => onSortChange(option)}
                style={{
                  background: sortValue.value === option.value
                    ? 'var(--primary-gradient)'
                    : 'var(--button-secondary-bg)',
                  border: sortValue.value === option.value
                    ? 'none'
                    : '1px solid var(--button-secondary-border)',
                  color: sortValue.value === option.value
                    ? 'var(--button-text)'
                    : 'var(--button-secondary-text)'
                }}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-4 w-full lg:w-auto lg:flex-row lg:items-center">
            <div className="relative w-full lg:w-64">
              <input
                className="w-full p-3 pl-10 rounded-lg focus:outline-none"
                placeholder="Search Token"
                onChange={onSearchChanged}
                style={{
                  background: 'var(--input-bg)',
                  border: '1px solid var(--input-border)',
                  color: 'var(--text-primary)'
                }}
              />
              <svg
                className="absolute left-3 top-3.5 w-5 h-5"
                style={{ color: 'var(--text-secondary)' }}
                fill="currentColor"
                viewBox="0 0 18 18"
              >
                <g>
                  <path d="M7.75987 15.5197C3.48078 15.5197 0 12.039 0 7.75987C0 3.48078 3.48078 0 7.75987 0C12.039 0 15.5197 3.48078 15.5197 7.75987C15.5197 12.039 12.039 15.5197 7.75987 15.5197ZM7.75987 1.90911C4.53381 1.90911 1.90911 4.53381 1.90911 7.75855C1.90911 10.9833 4.53381 13.608 7.75987 13.608C10.9859 13.608 13.6106 10.9833 13.6106 7.75855C13.6106 4.53381 10.9859 1.91042 7.75987 1.91042V1.90911Z"></path>
                  <path d="M16.8235 17.9987C16.5228 17.9987 16.2221 17.8845 15.9924 17.6547L11.9378 13.6001C11.4783 13.1406 11.4783 12.3974 11.9378 11.9379C12.3974 11.4783 13.1418 11.4783 13.6001 11.9379L17.6546 15.9924C18.1142 16.452 18.1142 17.1951 17.6546 17.6547C17.4249 17.8845 17.1242 17.9987 16.8235 17.9987Z"></path>
                </g>
              </svg>
            </div>

              <button
                className="p-2 rounded-lg flex items-center justify-center"
                onClick={onOrderChange}
                style={{
                  background: 'var(--button-secondary-bg)',
                  border: '1px solid var(--button-secondary-border)',
                  color: 'var(--button-secondary-text)',
                  minWidth: '44px'
                }}
              title={orderValue.value === 'Ascending' ? 'Sort Ascending' : 'Sort Descending'}
              >
                {orderValue.value === 'Ascending' ? (
                  <svg fill="currentColor" viewBox="0 0 21 17" width="21">
                    <g>
                      <path d="M17.2974 12.6684L19.0757 10.8901C19.5165 10.4493 20.2292 10.4493 20.67 10.8901C21.1109 11.331 21.1109 12.0437 20.67 12.4845L17.0607 16.0925C16.9025 16.2953 16.6807 16.4387 16.4306 16.4982C15.8274 16.651 15.2148 16.2872 15.062 15.6841C15.0228 15.5299 15.0174 15.3703 15.0444 15.2135V1.12783C15.0444 0.505763 15.5488 0 16.1723 0C16.7957 0 17.3001 0.504411 17.3001 1.12783V12.6684H17.2974ZM1.12783 1.45373H10.8942C11.5163 1.45373 12.022 1.95814 12.022 2.58156C12.022 3.20497 11.5176 3.70938 10.8942 3.70938H1.12783C0.504411 3.70803 0 3.20362 0 2.5802C0 1.95679 0.504411 1.45373 1.12783 1.45373ZM1.12783 7.46474H10.8942C11.5163 7.44446 12.0383 7.93264 12.0585 8.5547C12.0788 9.17677 11.5906 9.69876 10.9686 9.71904C10.9442 9.71904 10.9185 9.71904 10.8942 9.71904H1.12783C0.505763 9.71904 0 9.21463 0 8.59122C0 7.9678 0.504411 7.46339 1.12783 7.46339V7.46474ZM1.12783 13.4758H10.8942C11.5163 13.4758 12.022 13.9802 12.022 14.6036C12.022 15.227 11.5176 15.7314 10.8942 15.7314H1.12783C0.505763 15.7314 0 15.227 0 14.6036C0 13.9802 0.504411 13.4758 1.12783 13.4758Z"></path>
                    </g>
                  </svg>
                ) : (
                  <svg fill="currentColor" viewBox="0 0 21 17" width="21" style={{ transform: 'rotate(180deg)' }}>
                    <g>
                      <path d="M17.2974 12.6684L19.0757 10.8901C19.5165 10.4493 20.2292 10.4493 20.67 10.8901C21.1109 11.331 21.1109 12.0437 20.67 12.4845L17.0607 16.0925C16.9025 16.2953 16.6807 16.4387 16.4306 16.4982C15.8274 16.651 15.2148 16.2872 15.062 15.6841C15.0228 15.5299 15.0174 15.3703 15.0444 15.2135V1.12783C15.0444 0.505763 15.5488 0 16.1723 0C16.7957 0 17.3001 0.504411 17.3001 1.12783V12.6684H17.2974ZM1.12783 1.45373H10.8942C11.5163 1.45373 12.022 1.95814 12.022 2.58156C12.022 3.20497 11.5176 3.70938 10.8942 3.70938H1.12783C0.504411 3.70803 0 3.20362 0 2.5802C0 1.95679 0.504411 1.45373 1.12783 1.45373ZM1.12783 7.46474H10.8942C11.5163 7.44446 12.0383 7.93264 12.0585 8.5547C12.0788 9.17677 11.5906 9.69876 10.9686 9.71904C10.9442 9.71904 10.9185 9.71904 10.8942 9.71904H1.12783C0.505763 9.71904 0 9.21463 0 8.59122C0 7.9678 0.504411 7.46339 1.12783 7.46339V7.46474ZM1.12783 13.4758H10.8942C11.5163 13.4758 12.022 13.9802 12.022 14.6036C12.022 15.227 11.5176 15.7314 10.8942 15.7314H1.12783C0.505763 15.7314 0 15.227 0 14.6036C0 13.9802 0.504411 13.4758 1.12783 13.4758Z"></path>
                    </g>
                  </svg>
                )}
              </button>
          </div>
        </div>

        {/* Projects Grid */}
        {sortedFunLists.length > 0 ? (
          <>
            <LaunchpadCardGrid items={sortedFunLists} />
            {loading ? (
              <div className="flex justify-center py-8">
                <ClipLoader color="var(--text-accent)" size={50} />
              </div>
            ) : totalLength > 24 && totalLength - currentIndex > 0 ? (
              <div className="text-center mt-8">
                <button
                  onClick={loadMoreLists()}
                  className="px-6 py-3 rounded-lg font-medium transition-all hover:opacity-80"
                  style={{
                    background: 'var(--button-secondary-bg)',
                    border: '1px solid var(--button-secondary-border)',
                    color: 'var(--button-secondary-text)'
                  }}
                >
                  Load more...
                </button>
              </div>
            ) : null}
          </>
        ) : (
          <div className="text-center py-12" style={{ color: 'var(--text-secondary)' }}>
            No projects found
          </div>
        )}
      </div>
      </div>
      <Footer />
    </div>
  );
};

export default App;
